import { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { contactInfo } from "../data";

export default function Navbar() {
  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const location = useLocation();

  // Håndter scroll-effekt på navbar
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 20) {
        setIsScrolled(true);
      } else {
        setIsScrolled(false);
      }
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Luk burgermenu og dropdown, når ruten ændres
  useEffect(() => {
    setIsOpen(false);
    setIsDropdownOpen(false);
  }, [location]);

  // Tjek om en af ydelses-undersiderne er aktiv
  const isYdelserActive =
    location.pathname === "/service-og-reparation" ||
    location.pathname === "/daek-og-hjul" ||
    location.pathname === "/synsklargoering";

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-40 transition-all duration-300 ${
        isScrolled
          ? "bg-white/95 backdrop-blur-md shadow-md py-3"
          : "bg-transparent py-5"
      }`}
    >
      {/* Top Bar med hurtig info (kun desktop) */}
      <div className="border-b border-stone-200/50 pb-2 mb-3 hidden min-[1200px]:block">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-6 text-xs text-stone-500 font-medium">
          <div className="flex items-center gap-6">
            <span className="whitespace-nowrap">
              <i className="fa-solid fa-phone text-brand-orange mr-1.5" />
              Ring direkte: <a href={`tel:+45${contactInfo.phone.replace(/\s/g, "")}`} className="hover:text-brand-green transition font-bold">{contactInfo.phone}</a>
            </span>
            <span className="whitespace-nowrap">
              <i className="fa-solid fa-envelope text-brand-orange mr-1.5" />
              E-mail: <a href={`mailto:${contactInfo.email}`} className="hover:text-brand-green transition">{contactInfo.email}</a>
            </span>
            <span className="whitespace-nowrap">
              <i className="fa-solid fa-location-dot text-brand-orange mr-1.5" />
              {contactInfo.address}, {contactInfo.postalCode}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <span className="inline-block h-2 w-2 rounded-full bg-emerald-500 animate-pulse" />
            <span className="text-stone-600">Værkstedet er åbent i dag</span>
          </div>
        </div>
      </div>

      <div className="mx-auto flex max-w-7xl items-center justify-between px-6">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-3 group">
          <img
            src="https://assets.auxodev.dk/assets/45330f91-18ad-4301-b0b9-f03458bae5a3/logo-1.png"
            alt="Grønhøjauto Logo"
            className="h-12 w-auto object-contain md:h-14 transition duration-300 group-hover:scale-102"
          />
        </Link>

        {/* Desktop Menu (vises kun ved skærme >= 1200px) */}
        <nav className="hidden min-[1200px]:flex items-center gap-8">
          <NavLink
            to="/"
            className={({ isActive }) =>
              `text-sm font-bold tracking-wide transition-colors duration-200 hover:text-brand-orange ${
                isActive ? "text-brand-orange" : "text-brand-stone-dark"
              }`
            }
          >
            Forside
          </NavLink>

          <NavLink
            to="/vaerkstedet"
            className={({ isActive }) =>
              `text-sm font-bold tracking-wide transition-colors duration-200 hover:text-brand-orange ${
                isActive ? "text-brand-orange" : "text-brand-stone-dark"
              }`
            }
          >
            Værkstedet
          </NavLink>

          {/* Dropdown Menu til Ydelser */}
          <div
            className="relative"
            onMouseEnter={() => setIsDropdownOpen(true)}
            onMouseLeave={() => setIsDropdownOpen(false)}
          >
            <button
              className={`flex items-center gap-1.5 text-sm font-bold tracking-wide transition-colors duration-200 hover:text-brand-orange cursor-pointer ${
                isYdelserActive ? "text-brand-orange" : "text-brand-stone-dark"
              }`}
            >
              Ydelser
              <i className={`fa-solid fa-chevron-down text-xs transition-transform duration-200 ${isDropdownOpen ? "rotate-180" : ""}`} />
            </button>

            {/* Dropdown panel */}
            <div
              className={`absolute left-0 top-full pt-2 w-64 transition-all duration-200 ${
                isDropdownOpen
                  ? "opacity-100 translate-y-0 pointer-events-auto"
                  : "opacity-0 translate-y-2 pointer-events-none"
              }`}
            >
              <div className="rounded-xl border border-stone-100 bg-white p-2 shadow-lg">
                <NavLink
                  to="/service-og-reparation"
                  className={({ isActive }) =>
                    `block rounded-lg px-4 py-2.5 text-sm font-semibold transition hover:bg-stone-50 hover:text-brand-orange ${
                      isActive ? "text-brand-orange bg-stone-50/50" : "text-stone-700"
                    }`
                  }
                >
                  Service og reparation
                </NavLink>
                <NavLink
                  to="/daek-og-hjul"
                  className={({ isActive }) =>
                    `block rounded-lg px-4 py-2.5 text-sm font-semibold transition hover:bg-stone-50 hover:text-brand-orange ${
                      isActive ? "text-brand-orange bg-stone-50/50" : "text-stone-700"
                    }`
                  }
                >
                  Dæk og hjul
                </NavLink>
                <NavLink
                  to="/synsklargoering"
                  className={({ isActive }) =>
                    `block rounded-lg px-4 py-2.5 text-sm font-semibold transition hover:bg-stone-50 hover:text-brand-orange ${
                      isActive ? "text-brand-orange bg-stone-50/50" : "text-stone-700"
                    }`
                  }
                >
                  Synsklargøring
                </NavLink>
              </div>
            </div>
          </div>

          <NavLink
            to="/kontakt"
            className={({ isActive }) =>
              `text-sm font-bold tracking-wide transition-colors duration-200 hover:text-brand-orange ${
                isActive ? "text-brand-orange" : "text-brand-stone-dark"
              }`
            }
          >
            Kontakt
          </NavLink>

          {/* Book tid CTA i Navbar */}
          <Link
            to="/kontakt"
            state={{ subject: "Book tid" }}
            className="rounded-lg bg-brand-green px-5 py-2.5 text-sm font-bold text-white shadow-xs transition duration-200 hover:bg-brand-green-light cursor-pointer"
          >
            Book tid
          </Link>
        </nav>

        {/* Burgermenu Knap (vises kun ved skærme < 1200px) */}
        <div className="flex items-center gap-4 min-[1200px]:hidden">
          {/* Hurtig opkaldsknap på mobil */}
          <a
            href={`tel:+45${contactInfo.phone.replace(/\s/g, "")}`}
            className="flex h-10 w-10 items-center justify-center rounded-full bg-brand-green/10 text-brand-green transition hover:bg-brand-green/20"
            aria-label="Ring til værkstedet"
          >
            <i className="fa-solid fa-phone" />
          </a>

          <button
            onClick={() => setIsOpen(!isOpen)}
            className="flex h-10 w-10 items-center justify-center rounded-lg border border-stone-200 bg-white text-brand-stone-dark transition hover:bg-stone-50 cursor-pointer"
            aria-label="Åben menu"
          >
            <i className={`fa-solid ${isOpen ? "fa-xmark" : "fa-bars"} text-lg`} />
          </button>
        </div>
      </div>

      {/* Mobil Menu Panel (vises kun ved skærme < 1200px) */}
      <div
        className={`fixed inset-0 h-[100dvh] w-screen z-50 bg-white p-6 transition-all duration-300 min-[1200px]:hidden flex flex-col overflow-y-auto ${
          isOpen ? "opacity-100 translate-y-0 pointer-events-auto" : "opacity-0 -translate-y-4 pointer-events-none"
        }`}
      >
        {/* Mobil Menu Header */}
        <div className="flex items-center justify-between pb-4 border-b border-stone-100 mb-4">
          <Link to="/" onClick={() => setIsOpen(false)} className="flex items-center gap-3">
            <img
              src="https://assets.auxodev.dk/assets/45330f91-18ad-4301-b0b9-f03458bae5a3/logo-1.png"
              alt="Grønhøjauto Logo"
              className="h-12 w-auto object-contain"
            />
          </Link>
          <button
            onClick={() => setIsOpen(false)}
            className="flex h-10 w-10 items-center justify-center rounded-lg border border-stone-200 bg-white text-brand-stone-dark transition hover:bg-stone-50 cursor-pointer"
            aria-label="Luk menu"
          >
            <i className="fa-solid fa-xmark text-lg" />
          </button>
        </div>

        {/* Mobil Menu Links */}
        <div className="flex-1 overflow-y-auto pr-1">
          <nav className="flex flex-col gap-5">
            <NavLink
              to="/"
              onClick={() => setIsOpen(false)}
              className={({ isActive }) =>
                `text-base font-bold py-2 border-b border-stone-100 transition ${
                  isActive ? "text-brand-orange" : "text-brand-stone-dark"
                }`
              }
            >
              Forside
            </NavLink>

            <NavLink
              to="/vaerkstedet"
              onClick={() => setIsOpen(false)}
              className={({ isActive }) =>
                `text-base font-bold py-2 border-b border-stone-100 transition ${
                  isActive ? "text-brand-orange" : "text-brand-stone-dark"
                }`
              }
            >
              Værkstedet
            </NavLink>

            {/* Ydelser sektion i mobil menu */}
            <div className="py-2 border-b border-stone-100">
              <span className="text-base font-bold text-brand-stone-dark block mb-3">
                Ydelser
              </span>
              <div className="pl-4 flex flex-col gap-3.5">
                <NavLink
                  to="/service-og-reparation"
                  onClick={() => setIsOpen(false)}
                  className={({ isActive }) =>
                    `text-sm font-semibold transition ${
                      isActive ? "text-brand-orange" : "text-stone-600"
                    }`
                  }
                >
                  • Service og reparation
                </NavLink>
                <NavLink
                  to="/daek-og-hjul"
                  onClick={() => setIsOpen(false)}
                  className={({ isActive }) =>
                    `text-sm font-semibold transition ${
                      isActive ? "text-brand-orange" : "text-stone-600"
                    }`
                  }
                >
                  • Dæk og hjul
                </NavLink>
                <NavLink
                  to="/synsklargoering"
                  onClick={() => setIsOpen(false)}
                  className={({ isActive }) =>
                    `text-sm font-semibold transition ${
                      isActive ? "text-brand-orange" : "text-stone-600"
                    }`
                  }
                >
                  • Synsklargøring
                </NavLink>
              </div>
            </div>

            <NavLink
              to="/kontakt"
              onClick={() => setIsOpen(false)}
              className={({ isActive }) =>
                `text-base font-bold py-2 border-b border-stone-100 transition ${
                  isActive ? "text-brand-orange" : "text-brand-stone-dark"
                }`
              }
            >
              Kontakt
            </NavLink>

            {/* Mobil Info og CTA */}
            <div className="mt-6 space-y-4 rounded-xl bg-stone-50 p-4">
              <div className="text-xs text-stone-500 space-y-2">
                <p className="flex items-center gap-2">
                  <i className="fa-solid fa-phone text-brand-orange" />
                  Tlf: <span className="font-bold text-brand-stone-dark whitespace-nowrap">{contactInfo.phone}</span>
                </p>
                <p className="flex items-center gap-2">
                  <i className="fa-solid fa-envelope text-brand-orange" />
                  E-mail: <span className="text-brand-stone-dark whitespace-nowrap">{contactInfo.email}</span>
                </p>
                <p className="flex items-center gap-2">
                  <i className="fa-solid fa-clock text-brand-orange" />
                  <span>Man-Tor: 08-16 | Fre: 08-14.30</span>
                </p>
              </div>
              <Link
                to="/kontakt"
                state={{ subject: "Book tid" }}
                onClick={() => setIsOpen(false)}
                className="block w-full rounded-lg bg-brand-orange py-3 text-center text-sm font-bold text-white shadow-xs transition hover:bg-brand-orange-dark cursor-pointer"
              >
                Book tid nu
              </Link>
            </div>
          </nav>
        </div>
      </div>
    </header>
  );
}
